import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { LeagueContext, LeagueContextData } from '../../components/layouts/LeagueLayout';
import Card from '../../components/common/Card';
import GradingLoader from '../../components/common/GradingLoader';
import AnimatedNumber from '../../components/common/AnimatedNumber';
import { v1Api } from '../../services/api';
import { PlayerDetailResponse } from '../../types';

const POSITION_COLOR: { [key: string]: string } = {
  QB: '#A78BFA',
  RB: '#2FBF71',
  WR: '#5B8DEF',
  TE: '#E0A63F',
  K: '#7A8299',
  'D/ST': '#7A8299',
  DEF: '#7A8299',
  DST: '#7A8299',
};

/** Rank color by percentile within the weekly position pool. */
const rankColor = (rank?: number | null, pool?: number | null): string => {
  if (!rank || !pool || pool < 4) return '#8B92A8';
  const pct = (rank - 1) / (pool - 1);
  if (pct <= 0.25) return '#2FBF71'; // top quartile
  if (pct >= 0.75) return '#E05252'; // bottom quartile
  return '#8B92A8';
};

const PlayerPage: React.FC = () => {
  const { playerId, espnLeagueId } = useParams<{ playerId: string; espnLeagueId: string }>();
  const navigate = useNavigate();
  const context = useContext(LeagueContext) as LeagueContextData | null;
  const leagueId = context?.league?.id;

  const [player, setPlayer] = useState<PlayerDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;
    setPlayer(null);
    setError(null);
    v1Api.players
      .getPlayerDetail(parseInt(playerId), leagueId)
      .then((data) => {
        if (!cancelled) setPlayer(data);
      })
      .catch((err) => {
        console.error('Player detail fetch error:', err);
        if (!cancelled) setError('Failed to load player data');
      });
    return () => {
      cancelled = true;
    };
  }, [playerId, leagueId]);

  // Weeks with a real game (skip byes/DNP rows with 0 pts AND no start)
  const weeks = useMemo(
    () => (player?.weekly_stats ?? []).slice().sort((a, b) => a.week - b.week),
    [player]
  );
  const maxPoints = useMemo(
    () => Math.max(1, ...weeks.map((w) => w.points)),
    [weeks]
  );

  if (error) {
    return (
      <Card className="p-6 text-center">
        <div className="text-accent-danger mb-4 flex items-center justify-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-accent-primary text-background-primary rounded font-medium hover:brightness-110 transition-all active:translate-y-px"
        >
          Go back
        </button>
      </Card>
    );
  }

  if (!player) {
    return <GradingLoader label="Grading the player…" size="lg" className="py-24" />;
  }

  const posColor = POSITION_COLOR[player.position?.toUpperCase()] || '#7A8299';
  const stats = player.season_stats;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-text-secondary hover:text-text-primary transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
          <h1 className="text-4xl font-bold text-text-primary">{player.name}</h1>
          <span className="inline-flex items-center gap-1.5 text-base text-text-secondary">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: posColor }} />
            {player.position}
            <span className="text-text-tertiary">· {player.nfl_team}</span>
          </span>
          {player.injury_status && player.injury_status !== 'ACTIVE' && (
            <span className="px-2 py-0.5 rounded-md text-xs font-mono font-semibold uppercase bg-accent-danger/15 text-accent-danger border border-accent-danger/30">
              {player.injury_status}
            </span>
          )}
        </div>

        {player.draft_info && (
          <p className="font-mono text-sm text-text-secondary mt-2">
            R{player.draft_info.round} · P{player.draft_info.overall_pick} — drafted by{' '}
            <span className="text-text-primary">{player.draft_info.team.name}</span>
          </p>
        )}
      </div>

      {/* Season summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <p className="text-text-secondary text-sm">Total Points</p>
          <p className="text-2xl font-bold font-mono text-text-primary">
            <AnimatedNumber value={stats.total_points} />
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-text-secondary text-sm">Per Game</p>
          <p className="text-2xl font-bold font-mono text-text-primary">
            <AnimatedNumber value={stats.avg_points_per_game} />
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-text-secondary text-sm">Best Week</p>
          <p className="text-2xl font-bold font-mono text-accent-primary">
            <AnimatedNumber value={player.performance_highlights.best_week.points} />
          </p>
          <p className="text-xs text-text-tertiary font-mono">
            Week {player.performance_highlights.best_week.week}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-text-secondary text-sm">Worst Week</p>
          <p className="text-2xl font-bold font-mono text-accent-danger">
            <AnimatedNumber value={player.performance_highlights.worst_week.points} />
          </p>
          <p className="text-xs text-text-tertiary font-mono">
            Week {player.performance_highlights.worst_week.week}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-text-secondary text-sm">Starts</p>
          <p className="text-2xl font-bold font-mono text-text-primary">{stats.starts}</p>
          <p className="text-xs text-text-tertiary font-mono">
            {stats.start_percentage.toFixed(0)}% of weeks
          </p>
        </Card>
      </div>

      {/* Weekly bars */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Week by Week</h3>
        <div className="flex items-end gap-1.5 h-36">
          {weeks.map((w) => (
            <div key={w.week} className="group relative flex-1 flex flex-col items-center gap-1 min-w-0">
              <div
                className="w-full rounded-t-sm transition-opacity hover:opacity-80"
                style={{
                  height: `${Math.max(3, (w.points / maxPoints) * 100)}%`,
                  background: w.is_starter ? '#2FBF71' : '#5A6378',
                }}
                title={`Week ${w.week}: ${w.points.toFixed(1)} pts${w.is_starter ? ' (started)' : ' (benched)'}`}
              />
              <span className="font-mono text-[10px] text-text-tertiary">{w.week}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-text-secondary">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#2FBF71' }} /> Started
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#5A6378' }} /> Benched
          </span>
        </div>
      </Card>

      {/* Weekly table */}
      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-fieldline">
                <th className="text-left py-3 px-4 text-text-secondary font-medium text-sm">Week</th>
                <th className="text-right py-3 px-4 text-text-secondary font-medium text-sm">Points</th>
                <th className="text-right py-3 px-4 text-text-secondary font-medium text-sm">vs Proj</th>
                <th className="text-center py-3 px-4 text-text-secondary font-medium text-sm">
                  {player.position} Rank
                  <span className="block text-xs text-text-tertiary font-normal">that week</span>
                </th>
                <th className="text-center py-3 px-4 text-text-secondary font-medium text-sm">Slot</th>
                <th className="text-right py-3 px-4 text-text-secondary font-medium text-sm">Opp</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => {
                const projDelta = w.points - w.projected_points;
                return (
                  <tr key={w.week} className="border-b border-fieldline hover:bg-background-hover transition-colors">
                    <td className="py-3 px-4">
                      <span className="text-text-primary font-medium">Week {w.week}</span>
                      {w.matchup_info?.is_playoff && (
                        <span className="ml-2 font-mono text-[10px] uppercase text-accent-warning">Playoffs</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-text-primary">
                      {w.points.toFixed(1)}
                    </td>
                    <td
                      className="py-3 px-4 text-right font-mono"
                      style={{ color: projDelta >= 0 ? '#2FBF71' : '#E05252' }}
                    >
                      {projDelta >= 0 ? '+' : ''}
                      {projDelta.toFixed(1)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {w.position_rank && w.position_pool ? (
                        <span
                          className="font-mono font-semibold"
                          style={{ color: rankColor(w.position_rank, w.position_pool) }}
                        >
                          {player.position}
                          {w.position_rank}
                          <span className="text-text-tertiary font-normal">/{w.position_pool}</span>
                        </span>
                      ) : (
                        <span className="text-text-tertiary font-mono">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-mono font-bold ${
                          w.is_starter
                            ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
                            : 'bg-background-primary/30 text-text-secondary border border-fieldline'
                        }`}
                      >
                        {w.is_starter ? 'START' : 'BENCH'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-text-secondary">
                      {w.nfl_opponent || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default PlayerPage;
